import * as React from "react";
import { useQuery } from "react-query";
import { Select, Space, theme, Tag, Divider, Spin } from "antd";
import { Field, Condition, Comparator } from "src/search/types";
import { getFacetOptions } from "pages/api-clients/graph-search";
import { useApplicationContext } from "src/hooks/useApplication";
import { asKey } from "src/utils/fields";
import { columnInfo } from "@vzbl/integrations";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { DateRangeSelect } from "src/components/DateRangeSelect";
import { NumbersInput } from "./NumbersInput";
import { durationToRange } from "src/utils/date";
import { FieldSelector } from "./FieldSelector";
import styles from "./question-input.module.css";
import { FieldTooltip } from "./ClauseTooltips";
import { attributeToTitle, formatValue } from "src/utils/strings";
import { useEntitySelectors } from "src/search/useEntitySelectors";

export type FilterInputValue = {
  field?: Field;
  operator?: string;
  values?: any[];
};

// Create a global cache for formatted values
const valueFormatCache = new Map<string, string>();

// Memoized format function
const formatValueWithCache = (value: any): string => {
  const cacheKey = `${value}`;
  if (valueFormatCache.has(cacheKey)) {
    return valueFormatCache.get(cacheKey)!;
  }

  let formatted = value;
  if (value === "__VZBL::null__") {
    formatted = "N/A";
  } else if (typeof value === "string") {
    // Replace underscores with spaces first
    formatted = value.replace(/_/g, " ");

    // Split only on camelCase pattern (lowercase followed by uppercase)
    formatted = formatted.replace(/([a-z])([A-Z])/g, "$1 $2");

    // Don't split continuous capital letters
    formatted = formatted
      .split(/\s+/)
      .map((word: string) => {
        // If word is all caps, leave it as is
        if (/^[\dA-Z-]+$/.test(word)) {
          return word;
        }
        // Otherwise capitalize first letter
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ")
      .trim();
  }

  valueFormatCache.set(cacheKey, formatted);
  return formatted;
};

// Convert a condition to input type
export const conditionToInputType = (
  value: Condition | undefined
): FilterInputValue => {
  if (!value) {
    return {};
  }
  const operator = Object.keys(value.comparison || {})[0];
  const values = operator
    ? value.comparison[operator as keyof Comparator]
    : undefined;

  return {
    field: value
      ? ({
          target: value.target,
          attribute: value.attribute,
        } as Field)
      : undefined,
    operator: operator,
    values: values,
  };
};

export function FilterConditionPanel({
  initialValue,
  value,
  onApply,
  onCancel,
  showAppliedSummary = false,
  removeClause,
}: {
  initialValue: FilterInputValue;
  value: any;
  onApply: (data: FilterInputValue) => void;
  onCancel: () => void;
  showAppliedSummary?: boolean;
  removeClause?: (clause: any) => void;
}) {
  const { token } = theme.useToken();
  const orgId = useApplicationContext().org_id!;
  const integrationConfig = useIntegrationConfig();

  // Local state for the panel
  const [data, setData] = React.useState<FilterInputValue>(initialValue);

  // Reset data when panel opens with new initial value
  React.useEffect(() => {
    setData(initialValue);
  }, [initialValue]);

  // Get column metadata for the selected field
  const columnMeta = React.useMemo(
    () =>
      columnInfo(
        asKey(data.field || ({} as Field)),
        integrationConfig.data as any
      ),
    [data.field, integrationConfig.data]
  );

  // Handle field change
  const handleFieldChange = (field: Field | undefined) => {
    setData((current) => ({
      ...current,
      field,
      operator: undefined,
      values: undefined,
    }));
  };

  // Get available operators based on field type with descriptions
  const operators = React.useMemo(() => {
    const ops = [
      {
        value: "IN",
        label: "IS IN",
        description: "Select one or more values to include",
      },
      {
        value: "NOT IN",
        label: "NOT IN",
        description: "Select one or more values to exclude",
      },
    ];

    if (columnMeta.type === "number") {
      ops.push({
        value: "WITHIN",
        label: "BETWEEN",
        description: `Enter a range of numbers${
          columnMeta.unit ? ` (in ${columnMeta.unit})` : ""
        }`,
      });
    } else if (columnMeta.type === "datetime") {
      ops.push({
        value: "WITHIN",
        label: "BETWEEN",
        description: "Select a date range or duration",
      });
    }

    return ops;
  }, [columnMeta.type, columnMeta.unit]);

  const { filters: relevantFields } = useEntitySelectors({ state: value });

  // Optimize search function with better indexing
  const searchValues = React.useCallback((input: string, option: any) => {
    if (!input?.trim()) return true;
    if (!option) return false;

    const searchTerms = input.toLowerCase().split(/\s+/);
    const searchableText = option.label.toLowerCase();

    return searchTerms.every((term) => searchableText.includes(term));
  }, []);

  // Query for field values
  const query = useQuery(
    ["search/inputs/conditionForm", data.field?.target, data.field?.attribute],
    async () => {
      if (!data.field?.target || !data.field?.attribute) {
        return { data: [] };
      }
      return getFacetOptions(
        { select: value.select, search: value.search },
        {
          target: data.field.target,
          attribute: data.field.attribute,
        },
        orgId
      );
    },
    {
      enabled: Boolean(data.field?.target && data.field?.attribute),
    }
  );

  // Memoize and optimize options processing
  const selectOptions = React.useMemo(() => {
    if (!query.data?.data) return [];

    const uniqueOptions = new Map();

    // Process in chunks to avoid blocking the main thread
    const chunkSize = 200;
    const chunks = Math.ceil(query.data.data.length / chunkSize);

    for (let i = 0; i < chunks; i++) {
      const start = i * chunkSize;
      const end = start + chunkSize;
      const chunk = query.data.data.slice(start, end);

      chunk.forEach(({ value, label }) => {
        const formattedLabel = formatValueWithCache(label);
        if (!uniqueOptions.has(formattedLabel)) {
          uniqueOptions.set(formattedLabel, {
            value,
            label: formattedLabel,
            searchText: formattedLabel.toLowerCase(),
          });
        }
      });
    }

    return [...uniqueOptions.values()];
  }, [query.data?.data]);

  // Helper to format filter display
  const formatFilterDisplay = (filter: Condition) => {
    const operator = Object.keys(filter.comparison)[0];
    const values = filter.comparison[
      operator as keyof typeof filter.comparison
    ] as string[];
    return `${filter.target}.${filter.attribute} ${operator} ${values.join(
      ", "
    )}`;
  };

  // Render value selector with optimized configuration
  const renderValueSelector = () => {
    if (!data.field || !data.operator) return null;

    if (columnMeta.type === "number" && data.operator === "WITHIN") {
      return (
        <NumbersInput
          unit={columnMeta.unit}
          values={data.values || []}
          onChange={({ comparison }) => {
            const newValues = Object.values(comparison)[0];
            setData((current) => ({ ...current, values: newValues }));
          }}
          condition={
            {
              ...data.field,
              comparison: {
                [data.operator]: data.values,
              },
            } as Condition
          }
        />
      );
    }

    if (columnMeta.type === "datetime" && data.operator === "WITHIN") {
      return (
        <DateRangeSelect
          startDate={data.values?.[0]}
          endDate={data.values?.[1]}
          onChangeRange={(vals) =>
            setData((current) => ({ ...current, values: vals }))
          }
          onChangeDuration={(duration) => {
            const { startDate, endDate } = durationToRange(duration);
            setData((current) => ({
              ...current,
              values: [startDate, endDate],
            }));
          }}
        />
      );
    }

    return (
      <Select
        mode="multiple"
        style={{ width: "100%" }}
        showSearch
        maxTagCount="responsive"
        maxTagTextLength={20}
        listHeight={256}
        virtual={true}
        placeholder="Select values"
        value={data.values}
        onChange={(vals) =>
          setData((current) => ({ ...current, values: vals }))
        }
        options={selectOptions}
        filterOption={searchValues}
        optionFilterProp="searchText"
        maxTagPlaceholder={(omittedValues) =>
          `+ ${omittedValues.length} more...`
        }
        dropdownRender={(menu) =>
          query.isLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 160,
                padding: 24,
              }}
            >
              <Spin size="large" />
              <div
                style={{
                  marginTop: 16,
                  color: token.colorTextSecondary,
                  fontSize: 16,
                }}
              >
                Loading values...
              </div>
            </div>
          ) : (
            <div>
              {menu}
              {data.values && data.values.length > 0 && (
                <div
                  style={{
                    padding: "8px 12px",
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    fontSize: "0.85em",
                    color: token.colorTextDescription,
                  }}
                >
                  {data.values.length} selected
                </div>
              )}
            </div>
          )
        }
        loading={false}
        onPopupScroll={(e) => {
          // Add infinite scrolling logic here if needed
          const target = e.target as HTMLElement;
          if (
            target.scrollTop + target.offsetHeight >=
            target.scrollHeight - 100
          ) {
            // Load more data if needed
          }
        }}
      />
    );
  };

  const handleApply = () => {
    if (data.field && data.operator && data.values?.length) {
      onApply(data);
    }
  };

  // Handler to remove a filter from the summary
  const handleRemoveAppliedFilter = (key: string) => {
    if (removeClause) {
      // Find the clause by key in value.filters
      const clause = value.filters?.find((f: Condition) => f.key === key);
      if (clause)
        removeClause({
          key: `filter-${value.filters.indexOf(clause)}`,
          type: "filter-condition",
          value: clause,
        });
      return;
    }
    if (!Array.isArray(value.filters)) return;
    const newFilters = value.filters.filter((f: Condition) => f.key !== key);
    onApply({ filters: newFilters } as any);
  };

  return (
    <div className={styles.clausePanel} onClick={(e) => e.stopPropagation()}>
      {/* Applied Filters summary */}
      {showAppliedSummary &&
        Array.isArray(value.filters) &&
        value.filters.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Applied Filters:
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {value.filters.map((filter: Condition) => (
                <Tag
                  key={filter.key}
                  closable
                  onClose={() => handleRemoveAppliedFilter(filter.key)}
                  style={{
                    maxWidth: 220,
                    paddingRight: 24,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {formatFilterDisplay(filter)}
                  </span>
                </Tag>
              ))}
            </div>
            <Divider style={{ margin: "8px 0" }} />
          </div>
        )}
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Field Selection */}
        <div>
          <FieldTooltip type="filterField">
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Property
            </div>
          </FieldTooltip>
          <FieldSelector
            value={data.field}
            onChange={handleFieldChange}
            placeholder="Search for a field to filter by..."
            relevantFields={relevantFields}
          />
        </div>

        {/* Operator Selection */}
        {data.field && (
          <div>
            <FieldTooltip type="filterOperator">
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: token.colorTextSecondary,
                  marginBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Operator
              </div>
            </FieldTooltip>
            <Select
              style={{ width: "100%" }}
              placeholder="Choose how to filter..."
              value={data.operator}
              onChange={(op) =>
                setData((current) => ({
                  ...current,
                  operator: op,
                  values: undefined,
                }))
              }
              options={operators}
            />
          </div>
        )}

        {/* Value Selection */}
        {data.field && data.operator && (
          <div>
            <FieldTooltip type="filterValue">
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: token.colorTextSecondary,
                  marginBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Values
              </div>
            </FieldTooltip>
            {renderValueSelector()}
          </div>
        )}

        {/* Footer */}
        <div>
          {/* Summary of current filter */}
          {data.field && (
            <div
              className={styles.clauseSummary}
              style={{ background: token.colorBgContainerDisabled }}
            >
              <div>
                <span style={{ color: token.colorText }}>Filtering: </span>
                <span>
                  <strong>{attributeToTitle(data.field.attribute)}</strong>
                  <span style={{ opacity: 0.7 }}> from </span>
                  <strong>{attributeToTitle(data.field.target)}</strong>
                </span>
              </div>
              {data.operator && data.values && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ color: token.colorText }}>Condition: </span>
                  <span>
                    <span style={{ opacity: 0.7 }}>where value is </span>
                    <strong>{data.operator.toLowerCase()}</strong>
                    <span style={{ opacity: 0.7 }}> </span>
                    <strong>
                      {Array.isArray(data.values)
                        ? data.values
                            .map((v) => formatValueWithCache(v))
                            .join(", ")
                        : formatValueWithCache(data.values)}
                    </strong>
                  </span>
                </div>
              )}
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              paddingTop: 16,
              marginTop: 8,
            }}
          >
            <div
              style={{
                fontSize: "0.85em",
                color: token.colorTextDescription,
              }}
            >
              {(() => {
                if (!data.field) return "Select a field to begin";
                if (!data.operator) return "Choose how to filter";
                if (!data.values?.length) return "Select filter values";
                return "Ready to apply filter";
              })()}
            </div>
            <Space>
              <button
                onClick={onCancel}
                style={{
                  background: "none",
                  border: "none",
                  padding: "5px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  color: "rgba(0, 0, 0, 0.65)",
                  fontSize: 14,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  fontSize: 14,
                  backgroundColor: "#1677ff",
                  color: "white",
                  border: "none",
                  padding: "5px 16px",
                  borderRadius: 6,
                  cursor:
                    data.field && data.operator && data.values?.length
                      ? "pointer"
                      : "not-allowed",
                  opacity:
                    data.field && data.operator && data.values?.length
                      ? 1
                      : 0.5,
                  transition: "all 0.2s",
                  boxShadow: "0 2px 0 rgba(5, 145, 255, 0.1)",
                }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = "#4096ff";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = "#1677ff";
                  }
                }}
                disabled={!data.field || !data.operator || !data.values?.length}
                onClick={handleApply}
              >
                Apply
              </button>
            </Space>
          </div>
        </div>
      </Space>
    </div>
  );
}
